import React, { useMemo } from 'react';
import { Button, P, Tooltip } from '@fashionnovateam/nova-ui-toolkit';
import { DateTime } from 'luxon';
import {
  ItemLeadTime,
  CompletedItemLeadTime,
  EditedItemLeadTime,
  ItemLeadTimeErrors,
} from '../../types';
import { RequestedForCell } from '../../RequestedForCell';
import {
  CheckboxCell,
  EditableCell,
  ExclusivityToggle,
  ProductCell,
  ReorderLatestMessage,
  UnitsAdornment,
  WeeksAdornment,
  ReadOnlyShipmentMode,
} from '../cells';
import {
  formatCurrency,
  formatDate,
  formatRelativeTime,
  formatValue,
} from '../utils';
import { shipmentModeSelectOptions } from '../../constants';
import { BASELINE_COST_TOOLTIP } from '../../../../Lib/Constants/ui-text';
import { UpdateOneSchema } from '../../validation';

/**
 * Check if all required fields are filled for an item
 */
const checkItemReady = (
  item: ItemLeadTime | CompletedItemLeadTime,
): boolean => {
  const result = UpdateOneSchema.validate(item, { allowUnknown: true });
  return !result.error;
};

type UseTransformedRowsParams = {
  data: (ItemLeadTime | CompletedItemLeadTime)[];
  editedItems: EditedItemLeadTime[];
  errors: Record<string, ItemLeadTimeErrors>;
  isCompleted: boolean;
  canUpdate: boolean;
  canSubmit: boolean;
  isBaselineCostEnabled: boolean;
  vendorId: string;
  onRowClick?: (worksheetId: string) => void;
  onMessageClick?: (worksheetId: string) => void;
  onUpdateItem: (
    item: Partial<ItemLeadTime> & { worksheet_id: string },
  ) => void;
  onError: (worksheetId: string, errors: ItemLeadTimeErrors) => void;
  onSubmitItem: (item: ItemLeadTime) => void;
  isSubmitting: boolean;
  countrySelectItems: { value: string; label: string; disabled?: boolean }[];
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
};

export function useTransformedRows({
  data,
  editedItems,
  errors,
  isCompleted,
  canUpdate,
  canSubmit,
  isBaselineCostEnabled,
  vendorId,
  onRowClick,
  onMessageClick,
  onUpdateItem,
  onError,
  onSubmitItem,
  isSubmitting,
  countrySelectItems,
  selectedIds,
  onToggleRow,
}: UseTransformedRowsParams) {
  const getMergedItem = (item: ItemLeadTime | CompletedItemLeadTime) => {
    const editedItem = editedItems.find(
      (e) => e.worksheet_id === item.worksheet_id,
    );
    return { ...item, ...editedItem };
  };

  return useMemo(
    () =>
      data?.map((item) => {
        const mergedItem = getMergedItem(item);
        const itemErrors = errors[item.worksheet_id] || {};
        const isReady = checkItemReady(mergedItem);
        const isReadOnly = isCompleted || !canUpdate;
        const id = Object.hasOwn(item, 'id')
          ? (item as CompletedItemLeadTime).id
          : undefined;

        const rowData: Record<string, unknown> = {
          id: id ? `${id}-${item.worksheet_id}` : item.worksheet_id,
          ...(selectedIds &&
            onToggleRow && {
              checkbox: (
                <CheckboxCell
                  checked={selectedIds.has(item.worksheet_id)}
                  onChange={() => onToggleRow(item.worksheet_id)}
                />
              ),
            }),
          photo: {
            alt: mergedItem.style_name || 'Product',
            photo: mergedItem.photos?.[0] || '',
            onClick: () => {},
          },
          product: (
            <ProductCell
              styleName={mergedItem.style_name}
              styleColor={mergedItem.style_color}
              onClick={() => onRowClick?.(item.worksheet_id)}
            />
          ),
          'last_message.updated_at': (
            <ReorderLatestMessage
              vendorId={vendorId}
              styleColor={mergedItem.style_color}
              onClick={() =>
                (onMessageClick || onRowClick)?.(item.worksheet_id)
              }
              fallbackContent={mergedItem.last_message?.content}
              fallbackUpdatedAt={mergedItem.last_message?.updated_at}
            />
          ),
          requested_for: <RequestedForCell items={mergedItem.requested_for} />,
          cost_jr: isReadOnly ? (
            <P size="sm">{formatCurrency(mergedItem.cost_jr)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="cost_jr"
              value={mergedItem.cost_jr}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="currency"
            />
          ),
          cost_plus: isReadOnly ? (
            <P size="sm">{formatCurrency(mergedItem.cost_plus)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="cost_plus"
              value={mergedItem.cost_plus}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="currency"
            />
          ),
          country_of_origin: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.country_of_origin)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="country_of_origin"
              value={mergedItem.country_of_origin}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="select"
              options={countrySelectItems}
            />
          ),
          earliest_custom_cut_delivery_date: isReadOnly ? (
            <P size="sm">
              {formatDate(mergedItem.earliest_custom_cut_delivery_date)}
            </P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="earliest_custom_cut_delivery_date"
              value={
                mergedItem.earliest_custom_cut_delivery_date
                  ? DateTime.fromISO(
                      mergedItem.earliest_custom_cut_delivery_date,
                      { zone: 'utc' },
                    ).toFormat('yyyy-MM-dd')
                  : ''
              }
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="date"
            />
          ),
          custom_cut_lead_time_in_weeks: isReadOnly ? (
            <P size="sm">
              {mergedItem.custom_cut_lead_time_in_weeks
                ? `${mergedItem.custom_cut_lead_time_in_weeks} weeks`
                : '-'}
            </P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="custom_cut_lead_time_in_weeks"
              value={mergedItem.custom_cut_lead_time_in_weeks}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="number"
              endAdornment={<WeeksAdornment />}
            />
          ),
          shipment_mode: isReadOnly ? (
            <ReadOnlyShipmentMode
              isCompleted={isCompleted}
              value={mergedItem.shipment_mode}
            />
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="shipment_mode"
              value={mergedItem.shipment_mode}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="select"
              options={shipmentModeSelectOptions}
            />
          ),
          exclusivity: (
            <ExclusivityToggle
              worksheetId={item.worksheet_id}
              value={mergedItem.exclusivity}
              onUpdate={onUpdateItem}
              disabled={isReadOnly}
            />
          ),
          stock_quantity: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.stock_quantity)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="stock_quantity"
              value={mergedItem.stock_quantity}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="number"
              endAdornment={<UnitsAdornment />}
            />
          ),
          stock_ratio: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.stock_ratio)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="stock_ratio"
              value={mergedItem.stock_ratio}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              placeholder="0-0-0"
            />
          ),
          earliest_IHD_date_to_deliver_stock: isReadOnly ? (
            <P size="sm">
              {formatDate(mergedItem.earliest_IHD_date_to_deliver_stock)}
            </P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="earliest_IHD_date_to_deliver_stock"
              value={
                mergedItem.earliest_IHD_date_to_deliver_stock
                  ? DateTime.fromISO(
                      mergedItem.earliest_IHD_date_to_deliver_stock,
                      { zone: 'utc' },
                    ).toFormat('yyyy-MM-dd')
                  : ''
              }
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="date"
            />
          ),
          incoming_stock_quantity: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.incoming_stock_quantity)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="incoming_stock_quantity"
              value={mergedItem.incoming_stock_quantity}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="number"
              endAdornment={<UnitsAdornment />}
            />
          ),
          incoming_stock_ratio: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.incoming_stock_ratio)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="incoming_stock_ratio"
              value={mergedItem.incoming_stock_ratio}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              placeholder="0-0-0"
            />
          ),
          incoming_stock_date: isReadOnly ? (
            <P size="sm">{formatDate(mergedItem.incoming_stock_date)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="incoming_stock_date"
              value={
                mergedItem.incoming_stock_date
                  ? DateTime.fromISO(mergedItem.incoming_stock_date, {
                      zone: 'utc',
                    }).toFormat('yyyy-MM-dd')
                  : ''
              }
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="date"
            />
          ),
          custom_MOQ_jr: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.custom_MOQ_jr)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="custom_MOQ_jr"
              value={mergedItem.custom_MOQ_jr}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="number"
              endAdornment={<UnitsAdornment />}
            />
          ),
          custom_MOQ_plus: isReadOnly ? (
            <P size="sm">{formatValue(mergedItem.custom_MOQ_plus)}</P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="custom_MOQ_plus"
              value={mergedItem.custom_MOQ_plus}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              inputType="number"
              endAdornment={<UnitsAdornment />}
            />
          ),
          comments: isReadOnly ? (
            <P size="sm" className="truncate">
              {formatValue(mergedItem.comments)}
            </P>
          ) : (
            <EditableCell
              worksheetId={item.worksheet_id}
              field="comments"
              value={mergedItem.comments}
              onUpdate={onUpdateItem}
              onError={onError}
              errors={itemErrors}
              placeholder="Add comments"
            />
          ),
          submit:
            !isCompleted && canSubmit ? (
              <Button
                size="sm"
                color="secondary"
                onClick={() => onSubmitItem(mergedItem as ItemLeadTime)}
                disabled={!isReady || isSubmitting}
              >
                Submit
              </Button>
            ) : null,
        };

        if (isBaselineCostEnabled) {
          rowData.baseline_cost_jr = (
            <Tooltip content={BASELINE_COST_TOOLTIP} position="top" arrow>
              <P size="sm">{formatCurrency(mergedItem.baseline_cost_jr)}</P>
            </Tooltip>
          );
          rowData.baseline_cost_plus = (
            <Tooltip content={BASELINE_COST_TOOLTIP} position="top" arrow>
              <P size="sm">{formatCurrency(mergedItem.baseline_cost_plus)}</P>
            </Tooltip>
          );
        }

        rowData.requested_date = {
          date: mergedItem.requested_date,
          activity: false,
          time: false,
        };

        if (isCompleted) {
          rowData.updated_at = (
            <div className="flex items-center justify-center w-full h-full">
              <P size="sm" color="disabled">
                {formatRelativeTime(
                  (mergedItem as CompletedItemLeadTime).updated_at,
                )}
              </P>
            </div>
          );
        }

        return rowData;
      }),
    [
      data,
      editedItems,
      errors,
      isCompleted,
      canUpdate,
      canSubmit,
      isBaselineCostEnabled,
      vendorId,
      onRowClick,
      onMessageClick,
      onUpdateItem,
      onError,
      onSubmitItem,
      isSubmitting,
      countrySelectItems,
      selectedIds,
      onToggleRow,
    ],
  );
}
